/**
 * i18next translations backend — edge-first with api-gateway fallback (issue #292).
 *
 * Each locale's translation bundle is loaded from TWO possible sources, in order:
 *
 *   1. `/translations/<locale>.json` — a same-origin static asset baked into the
 *      Cloudflare Pages deployment (mirrors how `runtime-config.json` is baked).
 *      Served from the Cloudflare edge, so there is NO runtime dependency on the
 *      origin, and a new Pages deploy is the cache-bust.
 *   2. `<gateway>/config/<label>/translations/<locale>.json` — the ConfigService
 *      endpoint behind the api-gateway. Used locally / in dev where the asset
 *      isn't baked, and as a safety net.
 *
 * Why the ordering matters: ConfigService is scale-to-zero (min_instances=0,
 * #285/#286). Fetching the bundle from it on a cold start could fail/slip, and
 * the previous single-fetch-no-retry backend then stranded i18next on the en-US
 * fallback permanently for that page view — the storefront rendered entirely in
 * English with the locale pill stuck on "ENG" (reproduced on the #243 preview).
 * Reading the baked edge asset first removes that cold-start dependency.
 *
 * This module is deliberately free of import-time side effects so it can be
 * unit-tested directly with a stubbed fetch (unlike i18n/index.ts, whose eager
 * bootstrapI18n() hits the network).
 */

type FetchResponseLike = {
	ok: boolean;
	status: number;
	json: () => Promise<unknown>;
};

type FetchLike = (
	url: string,
	init?: { method?: string; credentials?: RequestCredentials },
) => Promise<FetchResponseLike>;

/** i18next custom-backend plugin shape (the subset we implement). */
export interface I18nextBackendModule {
	type: "backend";
	init: () => void;
	read: (
		locale: string,
		ns: string,
		callback: (error: unknown, data: unknown) => void,
	) => void;
}

/**
 * Same-origin static asset path for a locale bundle, baked into the Pages
 * deployment and served from the Cloudflare edge.
 */
export function edgeTranslationsPath(locale: string): string {
	return `/translations/${locale}.json`;
}

/**
 * ConfigService translations URL for a (label, locale) pair, at the gateway
 * root. `label` is the tenant *prefix* (folder under terraform/store-labels),
 * which is what ConfigService keys on — NOT the Medusa store name.
 */
export function buildTranslationsUrl(
	apiGatewayUrl: string,
	label: string,
	locale: string,
): string {
	const base = apiGatewayUrl.replace(/\/$/, "");
	return `${base}/config/${label}/translations/${locale}.json`;
}

/**
 * Ordered candidate URLs for a locale bundle: the baked edge asset FIRST, the
 * cold-startable api-gateway ConfigService SECOND.
 */
export function translationSources(
	apiGatewayUrl: string,
	label: string,
	locale: string,
): string[] {
	return [edgeTranslationsPath(locale), buildTranslationsUrl(apiGatewayUrl, label, locale)];
}

/**
 * Lightweight i18next backend that fetches each locale bundle from the ordered
 * sources, returning the first that resolves. `fetchImpl` is injectable for
 * unit tests; production uses the global fetch.
 */
export function createConfigServiceBackend(
	apiGatewayUrl: string,
	label: string,
	fetchImpl?: FetchLike,
): I18nextBackendModule {
	const doFetch: FetchLike =
		fetchImpl ?? ((url, init) => fetch(url, init) as unknown as Promise<FetchResponseLike>);

	return {
		type: "backend",
		init() {
			// no-op
		},
		read(locale, _ns, callback) {
			const sources = translationSources(apiGatewayUrl, label, locale);
			(async () => {
				let lastError: unknown = new Error(
					`No translation source resolved for locale '${locale}'`,
				);
				for (const url of sources) {
					try {
						const response = await doFetch(url, { method: "GET", credentials: "omit" });
						if (response.ok) {
							const body = await response.json();
							callback(null, body);
							return;
						}
						lastError = new Error(`HTTP ${response.status} for ${url}`);
					} catch (error) {
						lastError = error;
					}
				}
				callback(lastError, null);
			})();
		},
	};
}
